

import { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../FlightDeals/FlightDeals.css";
import { BiSolidPhoneCall } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import emailjs from "emailjs-com";
// Import the airports array instead of using API
import { airports } from "./Flight"; // Update this path to match your file structure
import axios from 'axios';
import { API_URL, API_KEY } from "../../axiosConfig";
// Import SweetAlert
import Swal from 'sweetalert2';

// Add this custom CSS to your component or import in your CSS file
const datePickerStyles = `
  .disabled-date-input {
    cursor: not-allowed !important;
    opacity: 0.6 !important;
    background-color: #f5f5f5 !important;
    pointer-events: none !important;
  }
  
  @media screen and (max-width: 767px) {
    .disabled-date-input {
      opacity: 0.4 !important;
    }
  }
`;

export default function BusinessDeal() {
  const navigate = useNavigate();
  const [isOneWay, setIsOneWay] = useState(false);
  const [depart, setDepart] = useState("London All Airport");
  const [arrive, setArrive] = useState("");
  const [departSuggestions, setDepartSuggestions] = useState([]);
  const [arriveSuggestions, setArriveSuggestions] = useState([]);
  const [isDisabled, setIsDisabled] = useState(false);
  const [leaveDate, setLeaveDate] = useState(null); // Date object
  const [returnDate, setReturnDate] = useState(null); // Date object
  const [isMobile, setIsMobile] = useState(false);
  const [departCode, setDepartCode] = useState("LHR"); // Default for London
  const [arriveCode, setArriveCode] = useState("");
  const [isLoading, setIsLoading] = useState(false); // Set to false since we're not loading from API
  const leaveDatePickerRef = useRef(null);
  const returnDatePickerRef = useRef(null);
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
    remarks: "",
    departFrom: "",
    arriveAt: "",
    leaveOn: "",
    returnOn: "",
  });

  // Check if the device is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Initial check
    checkIfMobile();

    // Add event listener for window resize
    window.addEventListener("resize", checkIfMobile);

    // Clean up
    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  // Inject our custom styles for disabled datepicker
  useEffect(() => {
    // Check if style already exists
    if (!document.getElementById("date-picker-disabled-styles")) {
      const styleElement = document.createElement("style");
      styleElement.id = "date-picker-disabled-styles";
      styleElement.innerHTML = datePickerStyles;
      document.head.appendChild(styleElement);

      return () => {
        const existingStyle = document.getElementById(
          "date-picker-disabled-styles"
        );
        if (existingStyle) {
          document.head.removeChild(existingStyle);
        }
      };
    }
  }, []);

  const validateName = (name) => {
    const regex = /^[A-Za-z\s]+$/;
    if (!name) return "*Name is required";
    if (!regex.test(name)) return "*Accept only alphabets";
    return "";
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return "*Email is required";
    if (!regex.test(email)) return "*Invalid email format";
    return "";
  };

  const validatePhone = (phone) => {
    const regex = /^\d{6,14}$/;
    if (!phone) return "*Phone is required";
    if (!regex.test(phone)) return "*Phone should be 6-14 digits";
    return "";
  };

  const validateRemarks = (remarks) => {
    return remarks.trim() ? "" : "";
  };

  const validateDepart = (depart) => {
    if (!depart) return "*Departure is required";
    return "";
  };

  const validateArrive = (arrive) => {
    if (!arrive) return "*Arrival is required";
    return "";
  };

  const validateLeaveDate = (leaveDate) => {
    if (!leaveDate) return "*Leave date is required";
    return "";
  };

  const validateReturnDate = (returnDate) => {
    if (!isOneWay && !returnDate) return "*Return date is required";
    return "";
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let error = "";

    switch (name) {
      case "name":
        error = validateName(value);
        break;
      case "email":
        error = validateEmail(value);
        break;
      case "phone":
        error = validatePhone(value);
        break;
      case "remarks":
        error = validateRemarks(value);
        break;
      case "departFrom":
        error = validateDepart(value);
        break;
      case "arriveAt":
        error = validateArrive(value);
        break;
      case "leaveOn":
        error = validateLeaveDate(value);
        break;
      case "returnOn":
        error = validateReturnDate(value);
        break;
      default:
        break;
    }

    setErrors({ ...errors, [name]: error });
  };

  const handleDepartChange = (e) => {
    const value = e.target.value;
    setDepart(value);
    handleInputChange({ target: { name: "departFrom", value } });
  
    if (value.trim() === "") {
      setDepartSuggestions([]);
      return;
    }
  
    // First, get matches that start with the input (case insensitive)
    const startsWithMatches = airports.filter(airport => 
      airport.toLowerCase().startsWith(value.toLowerCase())
    );
    
    // Then, get matches that include the input but don't start with it
    const includesMatches = airports.filter(airport => 
      airport.toLowerCase().includes(value.toLowerCase()) && 
      !airport.toLowerCase().startsWith(value.toLowerCase())
    );
    
    // Combine the two arrays, with startsWith matches first
    const allMatches = [...startsWithMatches, ...includesMatches].slice(0, 10);
    
    setDepartSuggestions(allMatches);
  };
  
  const handleArriveChange = (e) => {
    const value = e.target.value;
    setArrive(value);
    handleInputChange({ target: { name: "arriveAt", value } });
  
    if (value.trim() === "") {
      setArriveSuggestions([]);
      return;
    }
  
    // First, get matches that start with the input (case insensitive)
    const startsWithMatches = airports.filter(airport => 
      airport.toLowerCase().startsWith(value.toLowerCase())
    );
    
    // Then, get matches that include the input but don't start with it
    const includesMatches = airports.filter(airport => 
      airport.toLowerCase().includes(value.toLowerCase()) && 
      !airport.toLowerCase().startsWith(value.toLowerCase())
    );
    
    // Combine the two arrays, with startsWith matches first
    const allMatches = [...startsWithMatches, ...includesMatches].slice(0, 10);
    
    setArriveSuggestions(allMatches);
  };

  const handleAirportSelect = (airport, type) => {
    // Extract airport code from string (format: "Name Airport (CODE)")
    const codeMatch = airport.match(/\(([^)]+)\)$/);
    const airportCode = codeMatch ? codeMatch[1] : "";
    const airportName = airport;
    
    if (type === "departure") {
      setDepart(airportName);
      setDepartCode(airportCode);
      setDepartSuggestions([]);
      handleInputChange({ target: { name: "departFrom", value: airportName } });
      console.log("Selected departure airport:", airportName, airportCode);
    } else {
      setArrive(airportName);
      setArriveCode(airportCode);
      setArriveSuggestions([]);
      handleInputChange({ target: { name: "arriveAt", value: airportName } });
      console.log("Selected arrival airport:", airportName, airportCode);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    const formData = new FormData(e.target);
    let data = Object.fromEntries(formData.entries());
  
    // Format dates for email (local timezone)
    function formatDateForEmail(date) {
      if (!date) return "";
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
  
    // Add missing state values
    data.departFrom = depart;
    data.arriveAt = arrive;
    data.tripType = isOneWay ? "One Way" : "Return";
    data.leaveOn = formatDateForEmail(leaveDate);
    data.returnOn = formatDateForEmail(returnDate);
    data.class = formData.get("class") || "";
    data.addHotel = formData.get("addHotel") || "";
  
    // Validate all fields
    const newErrors = {
      name: validateName(data.name),
      email: validateEmail(data.email),
      phone: validatePhone(data.phone),
      remarks: validateRemarks(data.remarks),
      departFrom: validateDepart(data.departFrom),
      arriveAt: validateArrive(data.arriveAt),
      leaveOn: validateLeaveDate(data.leaveOn),
      returnOn: validateReturnDate(data.returnOn),
    };
  
    setErrors(newErrors);
  
    // Check for errors
    const hasErrors = Object.values(newErrors).some((error) => error !== "");
    if (hasErrors) {
      Swal.fire({
        title: 'Form Error',
        text: 'Please fill all required fields correctly.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }
  
    setIsDisabled(true);
    
    Swal.fire({
      title: 'Processing',
      text: 'Sending your flight request...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });
    
    // Format the date for consistent storage (UTC)
    function formatDateForStorage(date) {
      if (!date) return "";
      // Force date to be at noon to avoid timezone issues
      const d = new Date(date);
      d.setHours(12, 0, 0, 0);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
    
    const searchData = {
      origin: departCode || "LHR",
      originName: depart || "London All Airport",
      destination: arriveCode || "",
      destinationName: arrive || "",
      departureDate: leaveDate ? formatDateForStorage(leaveDate) : "",
      returnDate: returnDate ? formatDateForStorage(returnDate) : "",
      travelers: parseInt(document.querySelector('select[name="passengers"]').value) || 1,
      tripType: isOneWay ? "one-way" : "round-trip",
      travelClass: document.querySelector('select[name="class"]').value || "Economy"
    };
    
    console.log("Saving search data to session storage:", searchData);
    
    sessionStorage.setItem('flightSearchParams', JSON.stringify(searchData));
    
    emailjs
      .send("service_src72qi", "template_bum7xhh", data, "X5UqVmxpTiGClhNfI")
      .then((result) => {
        console.log("Email Sent Successfully:", result);
        Swal.fire({
          title: 'Success!',
          text: 'Your flight request has been sent successfully.',
          icon: 'success',
          confirmButtonText: 'Continue'
        }).then(() => {
          window.localStorage.setItem('forceReload', 'true');
          navigate("/thankyou");
        });
      })
      .catch((error) => {
        console.error("Email Sending Error:", error);
        Swal.fire({
          title: 'Error',
          text: 'There was a problem sending your request. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      })
      .finally(() => setIsDisabled(false));
  };

  // Modified custom date picker input with inline styles for disabled state
  const CustomDatePickerInput = ({
    value,
    onClick,
    placeholder,
    isDisabled,
  }) => (
    <input
      type="text"
      value={value}
      onClick={isDisabled ? null : onClick}
      placeholder={placeholder}
      readOnly
      className={`input ${isDisabled ? "disabled-date-input" : ""}`}
      style={
        isDisabled
          ? {
              cursor: "not-allowed",
              opacity: isMobile ? 0.4 : 0.6,
              backgroundColor: "#f5f5f5",
              pointerEvents: "none",
            }
          : {}
      }
    />
  );

  // Get current date at the start of the day
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const handleLeaveDateChange = (date) => {
    setLeaveDate(date);
    handleInputChange({ target: { name: "leaveOn", value: date } });

    // Reset return date if it's before the selected leave date
    if (returnDate && returnDate < date) {
      setReturnDate(null);
    }
  };

  const handleReturnDateChange = (date) => {
    setReturnDate(date);
    handleInputChange({ target: { name: "returnOn", value: date } });
  };

  const calladmin = () => {
    window.location.href = "tel:02039449931";
  };
  return (
    <div className="flight-deal-container">
      <div className="full-deal">
       <form className="flight-deal-form" onSubmit={handleSubmit}>
                 <h2 className="titlee">Unlock Incredible Business Class Flight Booking</h2>
                 <div className="trip-type">
                   <label>
                     <input
                       type="radio"
                       name="tripType"
                       checked={!isOneWay}
                       onChange={() => setIsOneWay(false)}
                     />
                     Return
                   </label>
                   <label>
                     <input
                       type="radio"
                       name="tripType"
                       checked={isOneWay}
                       onChange={() => setIsOneWay(true)}
                     />
                     One Way
                   </label>
                 </div>
       
                 <div className="form-grid">
                   <label className="relative">
                     Depart from *
                     <input
                       type="text"
                       autoComplete="off"
                       className="input"
                       value={depart}
                       onChange={handleDepartChange}
                       onFocus={() => {
                         if (depart === "London All Airport") {
                           setDepart("");
                         }
                       }}
                       name="departFrom"
                       disabled={isLoading}
                     />
                     {errors.departFrom && (
                       <span className="error">{errors.departFrom}</span>
                     )}
                     {departSuggestions.length > 0 && (
                       <ul className="absolute-dropdown bg-dark border rounded shadow-md max-h-48 overflow-y-auto z-10">
                         {departSuggestions.map((airport, index) => (
                           <li
                             key={index}
                             onClick={() =>
                               handleAirportSelect(airport, "departure")
                             }
                             className="p-2 hover:bg-gray-200 cursor-pointer"
                           >
                             {airport}
                           </li>
                         ))}
                       </ul>
                     )}
                   </label>
       
                   <label className="relative">
                     Arrive at *
                     <input
                       type="text"
                       autoComplete="off"
                       className="input"
                       value={arrive}
                       onChange={handleArriveChange}
                       name="arriveAt"
                       disabled={isLoading}
                     />
                     {errors.arriveAt && (
                       <span className="error">{errors.arriveAt}</span>
                     )}
                     {arriveSuggestions.length > 0 && (
                       <ul className="absolute-dropdown bg-dark border rounded shadow-md max-h-48 overflow-y-auto z-10">
                         {arriveSuggestions.map((airport, index) => (
                           <li
                             key={index}
                             onClick={() =>
                               handleAirportSelect(airport, "arrival")
                             }
                             className="p-2 hover:bg-gray-200 cursor-pointer"
                           >
                             {airport}
                           </li>
                         ))}
                       </ul>
                     )}
                   </label>
       
                   <label>
                     Leave on *
                     <div className="date-picker-container">
                       <DatePicker
                         selected={leaveDate}
                         onChange={handleLeaveDateChange}
                         dateFormat="dd/MM/yyyy"
                         placeholderText="DD/MM/YYYY"
                         name="leaveOn"
                         shouldCloseOnSelect={true}
                         withPortal={isMobile}
                         portalId="leave-date-portal"
                         ref={leaveDatePickerRef}
                         minDate={today} // This disables past dates
                         customInput={
                           <CustomDatePickerInput
                             placeholder="DD/MM/YYYY"
                             isDisabled={false}
                           />
                         }
                         popperClassName="date-picker-popper"
                         popperModifiers={{
                           preventOverflow: {
                             enabled: true,
                             escapeWithReference: false,
                             boundariesElement: "viewport",
                           },
                         }}
                       />
                       <span className="date-picker-icon">📅</span>
                     </div>
                     {errors.leaveOn && (
                       <span className="error">{errors.leaveOn}</span>
                     )}
                   </label>
       
                   {!isOneWay && (
                     <label>
                       Return on *
                       <div className="date-picker-container">
                         <DatePicker
                           selected={returnDate}
                           onChange={handleReturnDateChange}
                           dateFormat="dd/MM/yyyy"
                           placeholderText="DD/MM/YYYY"
                           minDate={leaveDate || today} // This ensures return date is either after selected leave date or today
                           disabled={!leaveDate}
                           name="returnOn"
                           shouldCloseOnSelect={true}
                           withPortal={isMobile}
                           portalId="return-date-portal"
                           ref={returnDatePickerRef}
                           customInput={
                             <CustomDatePickerInput
                               placeholder="DD/MM/YYYY"
                               isDisabled={!leaveDate}
                             />
                           }
                           popperClassName="date-picker-popper"
                           popperModifiers={{
                             preventOverflow: {
                               enabled: true,
                               escapeWithReference: false,
                               boundariesElement: "viewport",
                             },
                           }}
                         />
                         <span className="date-picker-icon">📅</span>
                       </div>
                       {errors.returnOn && (
                         <span className="error">{errors.returnOn}</span>
                       )}
                     </label>
                   )}
       
                   <label>
                     Class
                     <div className="select-container">
                       <select className="input" name="class">
                         {["Business", "First", "Economy"].map(
                           (cls) => (
                             <option key={cls}>{cls}</option>
                           )
                         )}
                       </select>
                       <span className="dropdown-icon">▼</span>
                     </div>
                   </label>
       
                   <label>
                     Passengers
                     <div className="select-container">
                       <select className="input" name="passengers">
                         {[1, 2, 3, 4].map((num) => (
                           <option key={num}>{num}</option>
                         ))}
                       </select>
                       <span className="dropdown-icon">▼</span>
                     </div>
                   </label>
       
                   <label>
                     Add hotel?
                     <select className="input" name="addHotel">
                       {["No", "Yes"].map((option) => (
                         <option key={option}>{option}</option>
                       ))}
                     </select>
                   </label>
       
                   {["Name *", "Email *", "Phone *", "Remarks"].map(
                     (placeholder, idx) => (
                       <label key={idx}>
                         {placeholder}
                         <input
                           type={
                             placeholder.includes("Email")
                               ? "email"
                               : placeholder.includes("Phone")
                               ? "tel"
                               : "text"
                           }
                           className="input"
                           autoComplete="off"
                           name={placeholder.split(" ")[0].toLowerCase()}
                           onChange={handleInputChange}
                         />
                         {errors[placeholder.split(" ")[0].toLowerCase()] && (
                           <span className="error">
                             {errors[placeholder.split(" ")[0].toLowerCase()]}
                           </span>
                         )}
                       </label>
                     )
                   )}
                 </div>
                 <div className="but">
                   <button
                     type="submit"
                     className="submit-buttonf"
                     disabled={isDisabled || isLoading}
                   >
                     {isDisabled ? "Please wait..." : isLoading ? "Loading..." : "GET BEST DEALS"}
                   </button>
                 
                   <button type="button" onClick={calladmin} className="submit-buttonf2"><BiSolidPhoneCall className="cal"/> 02039449931</button>
                 </div>
               </form>
               <div className="flight-deal-info">
                 <h3 className="info-titlef">Why Book With Us?</h3>
                 <ul className="info-list">
                   {[
                     "Luxury at the best price",
                     "Team of Travel Experts",
                     "Smooth booking",
                     "100% Secure Booking",
                   ].map((text, idx) => (
                     <li key={idx} className="info-list-item">
                       <span className="tick">✔ </span> {text}
                     </li>
                   ))}
                 </ul>
                 <img src="/protected.png" alt="logo" className="logo-d" />
                 <p className="p-d">
                   <strong>Your Privacy Matters:</strong> <br />
                   <strong>
                     See our
                     <a href="/privacy-policy"> Privacy Policy</a> &
                     <a href="/terms-and-conditions"> terms&conditions</a>.
                   </strong>
                 </p>
               </div>
             </div>
           </div>
         );
       }


